import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'am-search-multiselect',
  templateUrl: './search-multiselect.component.html',
  styleUrl: './search-multiselect.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchMultiselectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatInput, NgClass, MatIconButton, MatIcon],
  standalone: true,
})
export class SearchMultiselectComponent implements ControlValueAccessor, OnChanges {
  /**
   * Label of the search placeholder.
   */
  @Input() placeholderLabel = 'Search';

  @Input() parentHasBeenReset?: Observable<void>;

  @Input() parentHasBeenToggled?: Observable<boolean>;

  /**
   * Reference to the search input field.
   */
  @ViewChild('searchSelectInput', { read: ElementRef })
  searchSelectInput: ElementRef;

  matSelect = inject<MatSelect>(MatSelect);

  /**
   * Reference to the MatSelect options.
   */
  options: QueryList<MatOption>;

  private readonly _changeDetectorRef = inject(ChangeDetectorRef);

  private _value: string;
  /**
   * Previously selected values when using <mat-select [multiple]="true">.
   */
  private _previousSelectedValues: any[];

  /**
   * Event that emits when the current value changes.
   */
  private _change = new EventEmitter<string>();

  private _destroyRef = inject(DestroyRef);

  /**
   * Current search value.
   */
  get value(): string {
    return this._value;
  }

  onChange: (_: any) => void = (_: any) => {};

  onTouched: (_: any) => void = (_: any) => {};

  /**
   * Handles the key down event with MatSelect.
   * Allows e.g. selecting with enter key, navigation with arrow keys, etc.
   */
  handleKeydown(event: KeyboardEvent): void {
    if (event.keyCode === 32) {
      event.stopPropagation();
    }
  }

  onInputChange(value: string): void {
    const valueChanged = value !== this._value;
    if (valueChanged) {
      this._value = value;
      this.onChange(value);
      this._change.emit(value);
    }
  }

  onBlur(value: string): void {
    this.writeValue(value);
    this.onTouched(value);
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (_: any) => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    const valueChanged = value !== this._value;
    if (valueChanged) {
      this._value = value;
      this._change.emit(value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.parentHasBeenReset.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._previousSelectedValues = [];
    });

    if (changes['parentHasBeenToggled']) {
      this.parentHasBeenToggled.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(toggled => {
        if (toggled) {
          this._previousSelectedValues = this.matSelect.value;
        } else {
          this._previousSelectedValues = [];
        }
      });
    }

    // Detect changes when the input changes
    this._change.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._changeDetectorRef.detectChanges();
    });

    // When the select dropdown panel is opened or closed
    this.matSelect.openedChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(opened => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      opened ? this.focus() : this.reset();
    });

    // Set the first item active after the options changed
    this.matSelect.openedChange.pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.options = this.matSelect.options;
      this.options.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        const keyManager = this.matSelect._keyManager;
        if (keyManager && this.matSelect.panelOpen) {
          // avoid "expression has been changed" error
          setTimeout(() => {
            keyManager.setFirstItemActive();
          });
        }
      });
    });

    this._initMultipleHandling();
  }

  /**
   * Focuses the search input field.
   */
  focus(): void {
    if (!this.searchSelectInput) {
      return;
    }

    // focus
    this.searchSelectInput.nativeElement.focus();
  }

  /**
   * Resets the current search value.
   */
  reset(focus?: boolean): void {
    if (!this.searchSelectInput) {
      return;
    }
    this.searchSelectInput.nativeElement.value = '';
    this.onInputChange('');
    if (focus) {
      this.focus();
    }
  }

  /**
   * Initializes handling <mat-select [multiple]="true">
   * Note: to improve this code, mat-select should be extended to allow disabling resetting the selection while filtering.
   */
  private _initMultipleHandling(): void {
    if (
      this.matSelect.multiple &&
      this.matSelect.value?.length > 0 &&
      (this._previousSelectedValues == null || this._previousSelectedValues.length === 0)
    ) {
      this._previousSelectedValues = this.matSelect.value;
    }
    // If <mat-select [multiple]="true">
    // store previously selected values and restore them when they are deselected
    // because the option is not available while we are currently filtering.
    this.matSelect.valueChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(values => {
      if (this.matSelect.multiple) {
        let restoreSelectedValues = false;
        if (
          this._value &&
          this._value.length &&
          this._previousSelectedValues &&
          Array.isArray(this._previousSelectedValues)
        ) {
          if (!values || !Array.isArray(values)) {
            values = [];
          }
          const optionValues = this.matSelect.options.map(option => option.value);
          this._previousSelectedValues.forEach(previousValue => {
            if (values.indexOf(previousValue) === -1 && optionValues.indexOf(previousValue) === -1) {
              // If a value that was selected before is deselected and not found in the options, it was deselected
              // due to the filtering, so we restore it.
              values.push(previousValue);
              restoreSelectedValues = true;
            }
          });
        }

        if (restoreSelectedValues) {
          this.matSelect._onChange(values);
        }

        this._previousSelectedValues = values;
      }
    });
  }
}
